// src/pages/FormBuilder/formBuilderMockData.js

export const FORM_FIELD_TYPES = {
  TEXT: "text",
  EMAIL: "email",
  PHONE: "phone",
  NUMBER: "number",
  DECIMAL: "decimal",
  TEXTAREA: "textarea",
  SELECT: "select",
  MULTI_SELECT: "multi_select",
  RADIO: "radio",
  CHECKBOX: "checkbox",
  DATE: "date",
  FILE: "file",
  HEADING: "heading",
  PARAGRAPH: "paragraph",
  // Change #100018 - display-only HTML (a formatted table, a styled note).
  // Asks nothing; the markup lives in SettingsJson.html and is sanitised
  // before it is rendered.
  HTML: "html",
  // Change #100635 - display-only log of the equipment that was on site at
  // the job the form is filled in against (the job's Assets tab). The rows
  // arrive with the render bundle; nothing is stored in the definition.
  JOB_EQUIPMENT_LOG: "job_equipment_log",
  DIVIDER: "divider",
  CONSENT: "consent",
  HIDDEN: "hidden",
  // Task 24833 - "need a signature block so you can sign".
  SIGNATURE: "signature",
  // Task 24701 - a repeating group is a PAIR of layout markers. Everything
  // positioned between them repeats together. Membership is positional rather
  // than nested, so the canvas, drag and drop and SortOrder all stay flat.
  REPEAT_START: "repeat_start",
  REPEAT_END: "repeat_end",
  // Change #100304 - a section is a PAIR of layout markers too, paired on
  // repeatGroupKey exactly like a repeating group. The visibility condition
  // set on section_start applies to every field positioned up to its
  // section_end, so a block of related questions ("fire incident") is shown
  // or hidden by one rule instead of the same rule copied onto every field.
  SECTION_START: "section_start",
  SECTION_END: "section_end",
};

// The two markers are always created, moved and deleted as a pair - a lone one
// is a broken group.
export const REPEAT_GROUP_TYPES = [
  FORM_FIELD_TYPES.REPEAT_START,
  FORM_FIELD_TYPES.REPEAT_END,
];

// Change #100304 - same rule for a section's markers.
export const SECTION_TYPES = [
  FORM_FIELD_TYPES.SECTION_START,
  FORM_FIELD_TYPES.SECTION_END,
];

// Every paired marker type - the ones duplicated and deleted as a whole span.
export const PAIRED_MARKER_TYPES = [...REPEAT_GROUP_TYPES, ...SECTION_TYPES];

export const formBuilderPaletteGroups = [
  {
    id: "basic_fields",
    title: "Basic Fields",
    description: "Common fields for most forms.",
    items: [
      {
        id: "field_text",
        type: FORM_FIELD_TYPES.TEXT,
        title: "Text Input",
        subtitle: "Single line text field",
        icon: "fa-font",
        defaultConfig: {
          label: "Text field",
          required: false,
          helpText: "",
          width: "full",
        },
      },
      {
        id: "field_email",
        type: FORM_FIELD_TYPES.EMAIL,
        title: "Email",
        subtitle: "Email address field",
        icon: "fa-envelope",
        defaultConfig: {
          label: "Email address",
          required: true,
          helpText: "",
          width: "full",
        },
      },
      {
        id: "field_phone",
        type: FORM_FIELD_TYPES.PHONE,
        title: "Phone",
        subtitle: "Telephone number field",
        icon: "fa-phone",
        defaultConfig: {
          label: "Phone number",
          required: false,
          helpText: "",
          width: "full",
        },
      },
      {
        id: "field_number",
        type: FORM_FIELD_TYPES.NUMBER,
        title: "Number",
        subtitle: "Whole numbers only",
        icon: "fa-hashtag",
        defaultConfig: {
          label: "Number",
          required: false,
          helpText: "",
          width: "half",
          min: "",
          max: "",
        },
      },
      {
        id: "field_decimal",
        type: FORM_FIELD_TYPES.DECIMAL,
        title: "Decimal",
        subtitle: "Numbers with decimal places",
        icon: "fa-calculator",
        defaultConfig: {
          label: "Decimal",
          required: false,
          helpText: "",
          width: "half",
          min: "",
          max: "",
          decimals: "2",
        },
      },
      {
        id: "field_textarea",
        type: FORM_FIELD_TYPES.TEXTAREA,
        title: "Textarea",
        subtitle: "Long text response",
        icon: "fa-align-left",
        defaultConfig: {
          label: "Message",
          required: false,
          helpText: "",
          width: "full",
          rows: 4,
        },
      },
    ],
  },
  {
    id: "choice_fields",
    title: "Choices",
    description: "Dropdowns, options and selections.",
    items: [
      // Task 24707 - a yes/no question was a Dropdown plus retyping both
      // options every time. Radio Group already defaulted to Yes/No, but
      // nothing said so, and "Radio Group" is not what someone looks for when
      // they want a yes/no. This is that question as one click, first in the
      // group so it is found before the generic tiles.
      {
        id: "field_yes_no",
        type: FORM_FIELD_TYPES.RADIO,
        title: "Yes / No",
        subtitle: "A yes or no question",
        icon: "fa-toggle-on",
        defaultConfig: {
          label: "Yes or no?",
          required: false,
          helpText: "",
          width: "full",
          options: [
            {
              id: "option_yes",
              label: "Yes",
              value: "yes",
            },
            {
              id: "option_no",
              label: "No",
              value: "no",
            },
          ],
        },
      },
      {
        id: "field_select",
        type: FORM_FIELD_TYPES.SELECT,
        title: "Dropdown",
        subtitle: "Choose one option",
        icon: "fa-caret-down",
        defaultConfig: {
          label: "Select an option",
          required: false,
          helpText: "",
          width: "full",
          options: [
            {
              id: "option_1",
              label: "Option 1",
              value: "option_1",
            },
            {
              id: "option_2",
              label: "Option 2",
              value: "option_2",
            },
          ],
        },
      },
      {
        id: "field_multi_select",
        type: FORM_FIELD_TYPES.MULTI_SELECT,
        title: "Multi Select",
        subtitle: "Choose multiple options",
        icon: "fa-list-ul",
        defaultConfig: {
          label: "Select options",
          required: false,
          helpText: "",
          width: "full",
          options: [
            {
              id: "option_1",
              label: "Option 1",
              value: "option_1",
            },
            {
              id: "option_2",
              label: "Option 2",
              value: "option_2",
            },
          ],
        },
      },
      {
        id: "field_radio",
        type: FORM_FIELD_TYPES.RADIO,
        title: "Radio Group",
        subtitle: "Choose one visible option",
        icon: "fa-circle-dot",
        defaultConfig: {
          label: "Choose one",
          required: false,
          helpText: "",
          width: "full",
          options: [
            {
              id: "option_yes",
              label: "Yes",
              value: "yes",
            },
            {
              id: "option_no",
              label: "No",
              value: "no",
            },
          ],
        },
      },
      {
        id: "field_checkbox",
        type: FORM_FIELD_TYPES.CHECKBOX,
        title: "Checkbox",
        subtitle: "Single checkbox option",
        icon: "fa-square-check",
        defaultConfig: {
          label: "Checkbox option",
          checkboxText: "I agree",
          required: false,
          helpText: "",
          width: "full",
        },
      },
      {
        // Task 24833 - a form that asks someone to agree to something usually
        // wants them to SIGN it, not just tick a box. Sits next to Consent
        // because that is where someone looking for it will look.
        id: "field_signature",
        type: FORM_FIELD_TYPES.SIGNATURE,
        title: "Signature",
        subtitle: "Draw or type a signature",
        icon: "fa-signature",
        defaultConfig: {
          label: "Signature",
          required: true,
          helpText: "Sign with your finger, mouse, or type your name.",
          width: "full",
        },
      },
      {
        id: "field_consent",
        type: FORM_FIELD_TYPES.CONSENT,
        title: "Consent",
        subtitle: "Agreement or GDPR consent",
        icon: "fa-shield-halved",
        defaultConfig: {
          label: "Consent",
          checkboxText:
            "I agree that my submitted information can be used to contact me.",
          required: true,
          helpText: "",
          width: "full",
        },
      },
    ],
  },
  {
    id: "advanced_fields",
    title: "Advanced",
    description: "Useful fields for richer forms.",
    items: [
      {
        // Task 24696 - "is it worth having an address as a field type:
        // address line 1,2,3,4,town,city,postcode".
        //
        // It is, but as a PRESET rather than a composite field type.
        // FormSubmissionValue stores one ValueText per field, so a single
        // composite address would have to serialise seven answers into one
        // column and every downstream consumer - exports, the submissions
        // table, e-sign merge fields - would show a blob instead of a
        // postcode. Seven ordinary text fields keep their own values, so all
        // of that keeps working, and the author can rename, reorder or delete
        // any line afterwards.
        //
        // `preset` instead of `type` is what tells the canvas to expand it.
        id: "field_address",
        preset: [
          ["Address line 1", "full", true],
          ["Address line 2", "full", false],
          ["Address line 3", "full", false],
          ["Address line 4", "full", false],
          ["Town", "half", false],
          ["City", "half", false],
          ["Postcode", "half", true],
        ].map(([label, width, required]) => ({
          type: FORM_FIELD_TYPES.TEXT,
          defaultConfig: {
            label,
            required,
            helpText: "",
            width,
          },
        })),
        title: "Address",
        subtitle: "Seven fields: lines 1-4, town, city, postcode",
        icon: "fa-location-dot",
      },
      {
        id: "field_date",
        type: FORM_FIELD_TYPES.DATE,
        title: "Date",
        subtitle: "Date picker field",
        icon: "fa-calendar-days",
        defaultConfig: {
          label: "Date",
          required: false,
          helpText: "",
          width: "half",
        },
      },
      {
        id: "field_file",
        type: FORM_FIELD_TYPES.FILE,
        title: "File Upload",
        subtitle: "Allow upload attachment",
        icon: "fa-paperclip",
        defaultConfig: {
          label: "Upload file",
          required: false,
          helpText: "Accepted files will be configured later.",
          width: "full",
          multiple: false,
        },
      },
      {
        id: "field_hidden",
        type: FORM_FIELD_TYPES.HIDDEN,
        title: "Hidden Field",
        subtitle: "Store hidden tracking data",
        icon: "fa-eye-slash",
        defaultConfig: {
          label: "Hidden field",
          key: "source",
          value: "",
          width: "full",
        },
      },
    ],
  },
  {
    id: "layout_fields",
    title: "Layout",
    description: "Structure and presentation blocks.",
    items: [
      {
        id: "field_heading",
        type: FORM_FIELD_TYPES.HEADING,
        title: "Heading",
        subtitle: "Section title",
        icon: "fa-heading",
        defaultConfig: {
          label: "Section heading",
          level: "h2",
          width: "full",
        },
      },
      {
        id: "field_paragraph",
        type: FORM_FIELD_TYPES.PARAGRAPH,
        title: "Paragraph",
        subtitle: "Helpful text content",
        icon: "fa-paragraph",
        defaultConfig: {
          label:
            "Use this text to explain the form section or give users more context.",
          width: "full",
        },
      },
      {
        // Change #100018
        id: "field_html",
        type: FORM_FIELD_TYPES.HTML,
        title: "HTML Block",
        subtitle: "Tables and formatted content",
        icon: "fa-code",
        defaultConfig: {
          label: "HTML block",
          html:
            '<table>\n  <thead>\n    <tr><th>Option</th><th>What it means</th></tr>\n  </thead>\n  <tbody>\n    <tr><td>Standard</td><td>Replace this with your own content</td></tr>\n    <tr><td>Premium</td><td>Tables, lists, <strong>bold</strong> and links all work</td></tr>\n  </tbody>\n</table>',
          width: "full",
        },
      },
      {
        // Change #100635
        id: "field_job_equipment_log",
        type: FORM_FIELD_TYPES.JOB_EQUIPMENT_LOG,
        title: "Equipment on site",
        subtitle: "Log of the job's equipment",
        icon: "fa-toolbox",
        defaultConfig: {
          label: "Equipment on site",
          width: "full",
        },
      },
      {
        id: "field_divider",
        type: FORM_FIELD_TYPES.DIVIDER,
        title: "Divider",
        subtitle: "Visual separation line",
        icon: "fa-minus",
        defaultConfig: {
          label: "",
          width: "full",
        },
      },
      {
        // Change #100304
        id: "field_section",
        type: FORM_FIELD_TYPES.SECTION_START,
        title: "Section",
        subtitle: "Group fields and show or hide them together",
        icon: "fa-object-group",
        defaultConfig: {
          label: "New section",
          helpText: "",
          width: "full",
        },
      },
      {
        id: "field_repeat_group",
        type: FORM_FIELD_TYPES.REPEAT_START,
        title: "Repeating Group",
        subtitle: "A block that can be added more than once",
        icon: "fa-layer-group",
        defaultConfig: {
          label: "Repeating section",
          helpText: "",
          width: "full",
          repeatAddLabel: "Add another",
          repeatMinRows: 1,
          repeatMaxRows: "",
        },
      },
    ],
  },
];

// Pre-loaded (editable) message shown on the confirmation screen when a
// submission triggers an e-sign envelope, so the submitter knows to go and sign.
export const DEFAULT_ESIGN_CONFIRMATION =
  "Thanks! We've emailed you a document to review and electronically sign. Please check your inbox (and spam) to complete your e-signature.";

export const initialFormBuilderData = {
  id: "form_lead_capture",
  name: "Lead Capture Form",
  status: "draft",
  version: 1,
  publicToken: "",
  branding: {
    logoUrl: "",

    title: "Contact Us",
    description:
      "Send us your details and a member of our team will get back to you.",

    // Main layout colours
    primaryColor: "#2563eb",
    accentColor: "#16a34a",
    backgroundColor: "#f8fafc",
    cardColor: "#ffffff",

    // Text colours
    titleColor: "#0f172a",
    descriptionColor: "#64748b",
    textColor: "#0f172a",
    labelColor: "#0f172a",
    mutedTextColor: "#64748b",
    helpTextColor: "#64748b",
    requiredColor: "#ef4444",

    // Field styling
    fieldBackgroundColor: "transparent",
    inputBackgroundColor: "#ffffff",
    inputTextColor: "#0f172a",
    inputBorderColor: "#dbe3ef",
    inputFocusColor: "#2563eb",
    placeholderColor: "#64748b",

    // Borders / spacing
    borderColor: "#dbe3ef",
    borderRadius: 16,
    inputRadius: 12,
    fieldSpacing: 14,
    inputHeight: 44,

    // Button
    buttonText: "Submit",
    buttonStyle: "solid",
    buttonBackgroundColor: "#2563eb",
    buttonTextColor: "#ffffff",
    buttonBorderColor: "#2563eb",

    // Layout
    layout: "card",
  },
  fields: [
    {
      id: "field_name",
      type: FORM_FIELD_TYPES.TEXT,
      label: "Your name",
      required: true,
      helpText: "",
      width: "full",
    },
    {
      id: "field_email",
      type: FORM_FIELD_TYPES.EMAIL,
      label: "Email address",
      required: true,
      helpText: "",
      width: "full",
    },
    {
      id: "field_message",
      type: FORM_FIELD_TYPES.TEXTAREA,
      label: "How can we help?",
      required: false,
      helpText: "",
      width: "full",
      rows: 4,
    },
  ],
  submit: {
    successMessage: "Thanks, your form has been submitted.",
    createLead: true,
    createTask: false,
    notifyUserIds: ["user_admin"],
    redirectUrl: "",
  },
  // E-Sign on submit. Keys map 1:1 to the backend Form columns consumed by
  // FormESignLibrary::createEnvelopeFromSubmission (see docs/internal/Form_ESign_Bridge.md):
  //   enabled            -> FlagCreateESign
  //   signerSource       -> ESignSignerSource
  //   signerNameFieldKey -> ESignSignerNameFieldKey
  //   signerEmailFieldKey-> ESignSignerEmailFieldKey
  //   signerMobileFieldKey-> ESignSignerMobileFieldKey
  //   approveMode        -> ESignApproveMode
  //   reminderScheduleId -> ESignReminderSchedule_Id
  //   conditionFieldKey  -> ESignConditionFieldKey
  //   conditionOperator  -> ESignConditionOperator
  //   conditionValue     -> ESignConditionValue
  esign: {
    enabled: false,
    signerSource: "field",
    signerNameFieldKey: "",
    signerEmailFieldKey: "",
    signerMobileFieldKey: "",
    approveMode: "portal",
    reminderScheduleId: "",
    conditionFieldKey: "",
    conditionOperator: "any",
    conditionValue: "",
    confirmationMessage: DEFAULT_ESIGN_CONFIRMATION,
  },
};

export const esignSignerSourceOptions = [
  {
    value: "field",
    label: "From a form field",
  },
  {
    value: "submitter",
    label: "The person submitting the form",
  },
];

export const esignApproveModeOptions = [
  {
    value: "portal",
    label: "Portal (open signing page)",
  },
  {
    value: "one_click",
    label: "One-click approve / decline",
  },
];

export const esignConditionOperatorOptions = [
  {
    value: "any",
    label: "Always send",
  },
  {
    value: "equals",
    label: "Equals",
  },
  {
    value: "not_equals",
    label: "Does not equal",
  },
  {
    value: "greater_than",
    label: "Greater than",
  },
  {
    value: "less_than",
    label: "Less than",
  },
  {
    value: "contains",
    label: "Contains",
  },
];

export const formWidthOptions = [
  {
    value: "full",
    label: "Full width",
  },
  {
    value: "half",
    label: "Half width",
  },
  {
    value: "third",
    label: "Third width",
  },
];

export const formButtonStyleOptions = [
  {
    value: "solid",
    label: "Solid",
  },
  {
    value: "outline",
    label: "Outline",
  },
  {
    value: "soft",
    label: "Soft",
  },
];

export const formLayoutOptions = [
  {
    value: "card",
    label: "Card",
  },
  {
    value: "flat",
    label: "Flat",
  },
  {
    value: "split",
    label: "Split",
  },
];

export const formSubmitTargets = [
  {
    id: "lead",
    label: "Create Lead",
    description: "Create a CRMit lead from the submitted data.",
  },
  {
    id: "task",
    label: "Create Task",
    description: "Raise a follow-up task and send it to a team.",
  },
  {
    id: "email",
    label: "Send Notification",
    description: "Notify internal users when the form is submitted.",
  },
];
export const formFieldSpacingOptions = [
  {
    value: 8,
    label: "Compact",
  },
  {
    value: 14,
    label: "Comfortable",
  },
  {
    value: 22,
    label: "Spacious",
  },
];

export const formInputHeightOptions = [
  {
    value: 38,
    label: "Small",
  },
  {
    value: 44,
    label: "Default",
  },
  {
    value: 52,
    label: "Large",
  },
];